<script>
  import { onMount } from "svelte";
  import { apiRequest } from "../utils/api";
  import { groupDigits } from "../utils/format.js";

  export let isAdmin = false;

  // System-wide hard ceilings (mirrors LlmModel::SYSTEM_MAX_COST /
  // SYSTEM_MAX_STEPS / SYSTEM_MAX_TOTAL_TOKENS / SYSTEM_MAX_DURATION — the agent
  // clamps to these regardless of what's configured here, so the form only needs
  // to communicate the cap, not enforce it server-side).
  const SYSTEM_MAX_COST = 10;
  const SYSTEM_MAX_STEPS = 200;
  const SYSTEM_MAX_TOTAL_TOKENS = 2000000;
  const SYSTEM_MAX_DURATION_MINUTES = 240; // LlmModel::SYSTEM_MAX_DURATION = 4.hours

  // The column defaults for the four safety limits, mirroring the `default:` on
  // each column in db/schema.rb (`llm_models`), set by
  // db/migrate/20260723090000_create_llm_models.rb (budget/steps) and
  // db/migrate/20260729114910_add_tokens_and_duration_limits_to_llm_models.rb
  // (tokens/duration). A NEW model form opens pre-filled with these — ALL FOUR,
  // not two of four: every limit can end a run, so every limit is a visible
  // number the admin can see and change, rather than a value they inherit
  // without ever being shown it. That invisibility is precisely what made the
  // token ceiling stop run 33 with nobody aware it existed.
  //
  // They are duplicated here because the client cannot read a column default;
  // if a migration ever changes one, change it here too. The list screen always
  // shows what the SERVER actually stored, so a drift shows up there.
  const COLUMN_DEFAULT_LIMITS = {
    budget_limit: 10,
    steps_limit: 200,
    tokens_limit: 1000000,
    duration_limit: 120
  };

  const BLANK_FORM = {
    id: null,
    friendly_name: "",
    model: "",
    base_url: "",
    api_key: "",
    provider_pin: "",
    ...COLUMN_DEFAULT_LIMITS,
    position: 0
  };

  let models = [];
  let loading = false;
  let loadError = null;

  let showForm = false;
  let form = { ...BLANK_FORM };
  let saving = false;
  let saveError = null;

  let deletingId = null;
  let listActionError = null;

  onMount(() => {
    if (isAdmin) {
      loadModels();
    }
  });

  async function loadModels() {
    loading = true;
    loadError = null;
    try {
      models = await apiRequest("/api/llm_models");
    } catch (error) {
      loadError = error?.message || "Could not load LLM models.";
      models = [];
    } finally {
      loading = false;
    }
  }

  function openNewForm() {
    form = { ...BLANK_FORM, position: models.length };
    saveError = null;
    showForm = true;
  }

  function openEditForm(model) {
    form = {
      id: model.id,
      friendly_name: model.friendly_name || "",
      model: model.model || "",
      base_url: model.base_url || "",
      api_key: "",
      provider_pin: model.provider_pin || "",
      budget_limit: model.budget_limit ?? "",
      steps_limit: model.steps_limit ?? "",
      tokens_limit: model.tokens_limit ?? "",
      duration_limit: model.duration_limit ?? "",
      position: model.position ?? 0
    };
    saveError = null;
    showForm = true;
  }

  function closeForm() {
    showForm = false;
    form = { ...BLANK_FORM };
    saveError = null;
  }

  async function saveModel(event) {
    event?.preventDefault();
    if (saving) {
      return;
    }
    saving = true;
    saveError = null;

    const payload = {
      friendly_name: form.friendly_name,
      model: form.model,
      base_url: form.base_url,
      provider_pin: form.provider_pin,
      budget_limit: form.budget_limit,
      steps_limit: form.steps_limit,
      tokens_limit: form.tokens_limit,
      duration_limit: form.duration_limit,
      position: form.position
    };
    // A blank api_key must be omitted so the server keeps the existing stored
    // secret on update; on create an omitted key just leaves it unset.
    if (form.api_key.trim()) {
      payload.api_key = form.api_key;
    }

    try {
      if (form.id) {
        await apiRequest(`/api/llm_models/${form.id}`, { method: "PATCH", body: { llm_model: payload } });
      } else {
        await apiRequest("/api/llm_models", { method: "POST", body: { llm_model: payload } });
      }
      closeForm();
      await loadModels();
    } catch (error) {
      saveError = error?.message || "Could not save the model.";
    } finally {
      saving = false;
    }
  }

  async function deleteModel(model) {
    if (deletingId) {
      return;
    }
    deletingId = model.id;
    listActionError = null;
    try {
      await apiRequest(`/api/llm_models/${model.id}`, { method: "DELETE" });
      await loadModels();
    } catch (error) {
      listActionError = error?.message || "Could not delete the model.";
    } finally {
      deletingId = null;
    }
  }
</script>

{#if !isAdmin}
  <p class="text-neutral-500 text-xs">Admins only.</p>
{:else}
  <div class="space-y-4">
    <div class="flex items-center justify-between gap-3">
      <h3 class="text-xs font-bold text-neutral-800 uppercase tracking-wider">LLM models</h3>
      <button type="button" class="btn-primary py-1.5 px-3 text-xs" data-testid="llm-model-new" on:click={openNewForm}>
        + Add model
      </button>
    </div>

    <p class="text-[0.72rem] text-neutral-500 leading-relaxed">
      Each agent run picks one of these models. The base URL must <strong>not</strong> end in
      <code class="font-mono">/v1</code> — the OpenAI dialect appends it. The first model (by position) is the
      default a run falls back to when none is explicitly selected.
    </p>

    {#if loadError}
      <p class="bg-danger-bg text-danger-text border border-danger-border/20 rounded px-3 py-1.5 text-xs" role="alert">{loadError}</p>
    {/if}
    {#if listActionError}
      <p class="bg-danger-bg text-danger-text border border-danger-border/20 rounded px-3 py-1.5 text-xs" role="alert" data-testid="llm-model-list-error">{listActionError}</p>
    {/if}

    {#if showForm}
      <form class="bg-neutral-50 border border-neutral-200 rounded-lg p-4 space-y-3" on:submit={saveModel}>
        <h4 class="text-xs font-bold text-neutral-800 uppercase tracking-wider mb-1">
          {form.id ? "Edit model" : "New model"}
        </h4>

        {#if saveError}
          <p class="bg-danger-bg text-danger-text border border-danger-border/20 rounded px-3 py-1.5 text-xs" role="alert" data-testid="llm-model-save-error">{saveError}</p>
        {/if}

        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div class="form-group">
            <label class="form-label" for="llm-model-friendly-name">Friendly name</label>
            <input
              id="llm-model-friendly-name"
              class="form-input"
              placeholder="e.g. DeepSeek V4 Pro"
              bind:value={form.friendly_name}
              data-testid="llm-model-friendly-name"
              required
            />
          </div>

          <div class="form-group">
            <label class="form-label" for="llm-model-position">Position</label>
            <input id="llm-model-position" class="form-input" type="number" min="0" bind:value={form.position} />
          </div>

          <div class="form-group">
            <label class="form-label" for="llm-model-model">Model (API id)</label>
            <input
              id="llm-model-model"
              class="form-input font-mono"
              placeholder="provider/model"
              bind:value={form.model}
              required
            />
          </div>

          <div class="form-group">
            <label class="form-label" for="llm-model-base-url">Base URL</label>
            <input
              id="llm-model-base-url"
              class="form-input font-mono"
              placeholder="https://openrouter.ai/api"
              bind:value={form.base_url}
              required
            />
          </div>

          <div class="form-group">
            <label class="form-label" for="llm-model-api-key">API key</label>
            <input
              id="llm-model-api-key"
              class="form-input font-mono"
              type="password"
              autocomplete="new-password"
              placeholder={form.id ? "Leave blank to keep current key" : "Enter API key"}
              bind:value={form.api_key}
            />
          </div>

          <div class="form-group">
            <label class="form-label" for="llm-model-provider-pin">
              Provider pin <span class="font-normal text-neutral-400">(optional)</span>
            </label>
            <input
              id="llm-model-provider-pin"
              class="form-input font-mono"
              placeholder="e.g. openai/flex — leave blank to let the router choose"
              bind:value={form.provider_pin}
              data-testid="llm-model-provider-pin"
            />
            <p class="text-[0.68rem] text-neutral-500 leading-snug mt-1" data-testid="llm-model-provider-pin-hint">
              Pins every request to <strong>one</strong> OpenRouter endpoint, with no
              fallback. Keeps the prompt cache warm — hopping between backends arrives
              with a cold cache and that turn is billed at the full input rate. Which
              slugs are valid depends on <strong>your key's guardrail</strong>; check with
              <code>/api/v1/models/&lt;model&gt;/endpoints</code>.
            </p>
          </div>

          <!-- The FOUR safety limits, spanning the full form width so each keeps
               the width the original two had. Every one of them can end a run on
               its own, so they are configured together rather than two here and
               two hidden in the gem's defaults. -->
          <div class="md:col-span-2 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div class="form-group">
              <label class="form-label" for="llm-model-budget">Budget limit ($/run)</label>
              <input
                id="llm-model-budget"
                class="form-input"
                type="number"
                min="0"
                max={SYSTEM_MAX_COST}
                step="0.01"
                bind:value={form.budget_limit}
                data-testid="llm-model-budget"
                required
              />
              <p class="text-[0.68rem] text-neutral-500 leading-snug mt-1">Money spent by one run.</p>
            </div>
            <div class="form-group">
              <label class="form-label" for="llm-model-steps">Steps limit</label>
              <input
                id="llm-model-steps"
                class="form-input"
                type="number"
                min="1"
                max={SYSTEM_MAX_STEPS}
                step="1"
                bind:value={form.steps_limit}
                data-testid="llm-model-steps"
                required
              />
              <p class="text-[0.68rem] text-neutral-500 leading-snug mt-1">Agent turns in one run.</p>
            </div>
            <div class="form-group">
              <label class="form-label" for="llm-model-tokens">Token limit (per run)</label>
              <input
                id="llm-model-tokens"
                class="form-input"
                type="number"
                min="1"
                max={SYSTEM_MAX_TOTAL_TOKENS}
                step="1"
                bind:value={form.tokens_limit}
                data-testid="llm-model-tokens"
                required
              />
              <!-- MANDATORY wording (owner's decision, 2026-07-28): cached prompt
                   tokens ARE counted, so the number must be read as a worst case.
                   Without this note a reasoning model with 94% cache hits looks
                   ~20x cheaper in tokens than the ceiling actually treats it. -->
              <p class="text-[0.68rem] text-neutral-500 leading-snug mt-1" data-testid="llm-model-tokens-hint">
                Prompt + completion for the whole run, <strong>including cached tokens (worst case)</strong>.
              </p>
            </div>
            <div class="form-group">
              <label class="form-label" for="llm-model-duration">Time limit (minutes)</label>
              <input
                id="llm-model-duration"
                class="form-input"
                type="number"
                min="1"
                max={SYSTEM_MAX_DURATION_MINUTES}
                step="1"
                bind:value={form.duration_limit}
                data-testid="llm-model-duration"
                required
              />
              <p class="text-[0.68rem] text-neutral-500 leading-snug mt-1" data-testid="llm-model-duration-hint">
                <strong>Minutes of work</strong> — model generation plus tool calls (120 = 2
                hours). Time the run spends paused on an approval gate does not count.
              </p>
            </div>
          </div>
        </div>

        <p class="text-[0.72rem] text-neutral-500 leading-relaxed">
          Per-run caps — whichever is reached first ends the run. Capped at the system limits
          (${SYSTEM_MAX_COST} / {SYSTEM_MAX_STEPS} steps / {groupDigits(SYSTEM_MAX_TOTAL_TOKENS)} tokens /
          {SYSTEM_MAX_DURATION_MINUTES} minutes) regardless.
        </p>

        <div class="flex items-center justify-end gap-2 pt-1">
          <button type="button" class="btn-secondary py-1.5 px-3 text-xs" on:click={closeForm} disabled={saving}>
            Cancel
          </button>
          <button type="submit" class="btn-primary py-1.5 px-3 text-xs" data-testid="llm-model-save" disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    {/if}

    {#if loading}
      <div class="border border-neutral-200 rounded-lg overflow-hidden animate-pulse">
        <div class="bg-neutral-50 h-8 border-b border-neutral-200"></div>
        <div class="divide-y divide-neutral-100">
          {#each Array(3) as _}
            <div class="flex items-center p-3.5 space-x-4">
              <div class="h-4 bg-neutral-200 rounded w-1/4"></div>
              <div class="h-4 bg-neutral-200 rounded w-1/4"></div>
              <div class="h-4 bg-neutral-200 rounded w-1/6"></div>
              <div class="h-6 bg-neutral-200 rounded w-16 ml-auto"></div>
            </div>
          {/each}
        </div>
      </div>
    {:else if models.length === 0}
      <div class="bg-white border border-neutral-200 rounded-xl p-8 text-center space-y-2 shadow-2xs">
        <p class="text-neutral-500 text-xs font-semibold">No LLM models configured.</p>
        <p class="text-[0.7rem] text-neutral-400">Use "+ Add model" above — the agent has no provider to run against until one exists.</p>
      </div>
    {:else}
      <div class="overflow-x-auto border border-neutral-200 rounded-lg">
        <table class="w-full text-left border-collapse text-xs" data-testid="llm-model-table">
          <thead>
            <tr class="bg-neutral-50 border-b border-neutral-200">
              <th class="p-2.5 font-bold text-neutral-700">Friendly name</th>
              <th class="p-2.5 font-bold text-neutral-700">Model</th>
              <th class="p-2.5 font-bold text-neutral-700">Base URL</th>
              <th class="p-2.5 font-bold text-neutral-700">Budget</th>
              <th class="p-2.5 font-bold text-neutral-700">Steps</th>
              <th class="p-2.5 font-bold text-neutral-700 whitespace-nowrap" title="Prompt + completion, including cached tokens (worst case)">Tokens</th>
              <th class="p-2.5 font-bold text-neutral-700 whitespace-nowrap" title="Minutes of work: generation + tools, excluding time paused">Time</th>
              <th class="p-2.5 font-bold text-neutral-700">Key</th>
              <th class="p-2.5"></th>
            </tr>
          </thead>
          <tbody class="divide-y divide-neutral-100">
            {#each models as model, index (model.id)}
              <tr class="hover:bg-neutral-50/50 transition-colors" data-testid="llm-model-row" data-model-id={model.id}>
                <td class="p-2.5 font-medium text-neutral-900">
                  {model.friendly_name}
                  {#if index === 0}
                    <!-- Lowest position (server returns position ASC) = the model
                         pre-selected in new runs. Mark it so the admin knows. -->
                    <span class="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-[0.6rem] font-bold uppercase tracking-wider bg-info-bg text-info-text border border-info-border/20" data-testid="llm-model-default-badge" title="Pre-selected in new runs (lowest position)">Default</span>
                  {/if}
                </td>
                <td class="p-2.5 font-mono text-neutral-600">{model.model}</td>
                <td class="p-2.5 font-mono text-neutral-600">{model.base_url}</td>
                <td class="p-2.5">${Number(model.budget_limit).toFixed(2)}</td>
                <td class="p-2.5">{model.steps_limit}</td>
                <td class="p-2.5 whitespace-nowrap tabular-nums" data-testid="llm-model-tokens-cell">{groupDigits(model.tokens_limit)}</td>
                <td class="p-2.5 whitespace-nowrap tabular-nums" data-testid="llm-model-duration-cell">
                  {model.duration_limit == null ? "—" : `${groupDigits(model.duration_limit)} min`}
                </td>
                <td class="p-2.5">
                  {#if model.api_key_set}
                    <span class="inline-flex items-center px-2 py-0.5 rounded-full text-[0.68rem] font-bold uppercase tracking-wider bg-success-bg text-success-text border border-success-border/20">set</span>
                  {:else}
                    <span class="inline-flex items-center px-2 py-0.5 rounded-full text-[0.68rem] font-bold uppercase tracking-wider bg-warning-bg text-warning-text border border-warning-border/20">no key</span>
                  {/if}
                </td>
                <td class="p-2.5 text-right whitespace-nowrap">
                  <button type="button" class="btn-secondary py-1 px-2.5 text-[0.78rem]" on:click={() => openEditForm(model)}>
                    Edit
                  </button>
                  <button
                    type="button"
                    class="btn-secondary py-1 px-2.5 text-[0.78rem] ml-1.5 bg-danger-bg text-danger-text border border-danger-border/20"
                    data-testid="llm-model-delete"
                    disabled={deletingId === model.id}
                    on:click={() => deleteModel(model)}
                  >
                    {deletingId === model.id ? "Deleting…" : "Delete"}
                  </button>
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    {/if}
  </div>
{/if}
